/* eslint-disable camelcase */
import { ActionReducerMapBuilder, createAction } from '@reduxjs/toolkit'
import { quoteToStatusIdea, statusIdeaAdapter } from 'adapters'
import {
  Article,
  ARTICLE_TYPE,
  ContentItem,
  Gif,
  GIF_TYPE,
  LegacyStatusIdea,
  Photo,
  PHOTO_TYPE,
  RangeFilter,
  STATUS_TYPE,
  StatusIdea,
  Text,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { firstValueFrom } from 'rxjs'
import { HS_EVENT_CONTENT_FAVORITED, trackHubspotEvent } from 'services/tracking/hubspot'
import { createAppAsyncThunk } from 'store/state'
import { articlesAdapter } from '../adapters/articles'
import { gifsAdapter } from '../adapters/gifs'
import { photosAdapter } from '../adapters/photos'
import { textsAdapter } from '../adapters/texts'
import { videosAdapter } from '../adapters/videos'
import ContentState from '../state'
import { V1 } from './net'
import { ContentType, FilterType } from 'shared'

export const addFavoriteImmediate = createAction<ContentItem>('content/favorites/addFavoriteImmediate')
export const replaceLiveFavorite = createAction<{ type: ContentType, prevId: string, nextId: string, network: string }>(
  'content/favorites/replaceLiveFavorite'
)
export const removeFavoriteImmediate = createAction<{ id: string, type: ContentType }>('content/favorites/removeFavoriteImmediate')
export const undoRemoveFavorite = createAction<{ id: string, type: ContentType }>('content/favorites/undoRemoveFavorite')
export const getFavoritesSuccess = createAction<{ type: ContentType, items: string[] }>('content/favorites/getFavoritesSuccess')

export const favsExtraReducers = (builder: ActionReducerMapBuilder<ContentState>) => {
  builder
    .addCase(getFavorites.pending, state => {
      state.favorites.isFetching = true
    })
    .addCase(getFavorites.rejected, state => {
      state.favorites.isFetching = false
    })
    .addCase(addFavoriteImmediate, (state, action) => {
      const { type, rawId, live } = action.payload
      delete state.favorites.removed[type][rawId]
      state.favorites.added[type][rawId] = action.payload
      if (live) {
        state.favorites.live[rawId] = true
      }
    })
    .addCase(replaceLiveFavorite, (state, action) => {
      const { prevId, nextId, type, network } = action.payload
      const item = state.favorites.added[type][prevId]
      if (item) {
        delete state.favorites.added[type][prevId]
        state.favorites.added[type][nextId] = {
          ...item,
          rawId: nextId,
          id: `${network}:${nextId}`
        }
      }
    })
    .addCase(removeFavoriteImmediate, (state, action) => {
      const { id, type } = action.payload
      state.favorites.removed[type][id] = true
      delete state.favorites.added[type][id]
      delete state.favorites.live[id]
    })
    .addCase(undoRemoveFavorite, (state, action) => {
      const { id, type } = action.payload
      delete state.favorites.removed[type][id]
    })
    .addCase(getFavoritesSuccess, (state, action) => {
      const { type, items } = action.payload
      items.forEach(id => {
        state.favorites.saved[type][id] = true
      })
      state.favorites.isFetching = false
    })
}

export const addFavorite = createAppAsyncThunk(
  'content/favorites/addFavorite',
  async (item: ContentItem, { dispatch }) => {
    dispatch(addFavoriteImmediate(item))
    try {
      await firstValueFrom(dispatch(V1.addFavorite(item.rawId, item.type)))
      dispatch(trackHubspotEvent(HS_EVENT_CONTENT_FAVORITED))
    } catch (_e) {
      dispatch(removeFavoriteImmediate({ id: item.rawId, type: item.type }))
    }
  }
)

export const removeFavorite = createAppAsyncThunk(
  'content/favorites/removeFavorite',
  async (args: { item: ContentItem, live?: boolean }, { dispatch }) => {
    const { item, live } = args
    dispatch(removeFavoriteImmediate({ id: item.rawId, type: item.type }))
    try {
      if (live) {
        await firstValueFrom(dispatch(V1.removeFavoriteLive(item.rawId, item.type, item.feed.id)))
      } else {
        await firstValueFrom(dispatch(V1.removeFavorite(item.rawId, item.type)))
      }
    } catch (_e) {
      dispatch(undoRemoveFavorite({ id: item.rawId, type: item.type }))
    }
  }
)

export const getFavorites = createAppAsyncThunk(
  'content/favorites/getFavorites',
  async (args: { page: number, type: FilterType, range: RangeFilter }, { dispatch, rejectWithValue }) => {
    const { page, type, range } = args
    try {
      const response = await firstValueFrom(dispatch(V1.getFavorites(page, type, range)))
      let data: any = null
      switch (type) {
        case PHOTO_TYPE:
          data = photosAdapter(response)
          break
        case ARTICLE_TYPE:
          data = articlesAdapter(response)
          break
        case VIDEO_TYPE:
          data = videosAdapter(response)
          break
        case GIF_TYPE:
          data = gifsAdapter(response)
          break
        case TEXT_TYPE:
          data = textsAdapter(response)
          break
        default:
          throw new Error(`[getFavorites()] Error: Invalid type: ${type}`)
      }
      const ids = (data.items as any[]).map(i => i.rawId)
      dispatch(getFavoritesSuccess({ type, items: ids }))
      return data
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const addFavoriteLive = createAppAsyncThunk(
  'content/favorites/addFavoriteLive',
  async (item: Photo | Article | Video | Gif | Text, { dispatch }) => {
    const date = typeof item.createdAt === 'object' ? item.createdAt.toISOString() : new Date(item.createdAt).toISOString()
    const data = {
      content_item_type: item.type,
      feed_search_id: item.feed.id,
      source_id: item.feed.id,
      search_id: item.rawId,
      likes: item.likes,
      status: item.status,
      shares: item.shares,
      comments: item.comments,
      published_date: date,
      link: (item as Article).sourceLink || item.socialLink,
      title: (item as Article).title,
      image: item.imageUrl,
      feed_type: item.feed.type,
      details: '{}',
      object_id: item.rawId
    }

    dispatch(addFavoriteImmediate(item))
    try {
      const response = await firstValueFrom(dispatch(V1.addFavoriteLive(data)))
      if (response.contentItemId) {
        dispatch(replaceLiveFavorite({
          type: item.type,
          prevId: item.rawId,
          nextId: response.contentItemId.toString(),
          network: item.feed.type
        }))
      }
    } catch (_e) {
      dispatch(removeFavoriteImmediate({ id: item.rawId, type: item.type }))
    }
  }
)

export const getFavoriteStatusIdeas = createAppAsyncThunk(
  'content/favorites/getFavoriteStatusIdeas',
  async (page: number, { dispatch }) => {
    const response = await Promise.all([
      firstValueFrom(dispatch(V1.getFavoriteQuotes(page))),
      firstValueFrom(dispatch(V1.getFavoriteStatuses(page)))
    ])
    const quotes = response[0].map((q: any, index: number) => ({ ...quoteToStatusIdea(q, index), isFavorite: true }))
    const statuses = response[1].map((s: LegacyStatusIdea) => ({ ...statusIdeaAdapter(s), isFavorite: true }))
    const all = quotes.concat(statuses)

    dispatch(getFavoritesSuccess({ type: STATUS_TYPE as any, items: all.map((item: any) => item.id) }))
    return all
  }
)

export const addFavoriteStatusIdea = createAppAsyncThunk(
  'content/favorites/addFavoriteStatusIdea',
  async (item: StatusIdea, { dispatch }) => {
    dispatch(addFavoriteImmediate({ ...item, rawId: item.id } as any))
    const itemType = item.isQuote ? 'quote' : 'si'
    try {
      await firstValueFrom(dispatch(V1.addFavoriteStatusIdea(item.id, itemType)))
    } catch (_e) {
      dispatch(removeFavoriteImmediate({ id: item.id, type: item.type }))
    }
  }
)

export const removeFavoriteStatusIdea = createAppAsyncThunk(
  'content/favorites/removeFavoriteStatusIdea',
  async (item: StatusIdea, { dispatch }) => {
    dispatch(removeFavoriteImmediate({ id: item.id, type: item.type }))
    const itemType = item.isQuote ? 'quote' : 'si'
    try {
      await firstValueFrom(dispatch(V1.removeFavoriteStatusIdea(item.id, itemType)))
    } catch (_e) {
      dispatch(undoRemoveFavorite({ id: item.id, type: item.type }))
    }
  }
)
